import type {
  SampleCall,
  SampleDataFieldError,
  SampleDataTemplateContent,
  SampleDataTemplateResponse,
  SampleEvent,
  SampleGame,
  SampleNote,
  SamplePlayer,
  SampleRosterBoard,
  SampleRosterPlacement,
  SampleTeam,
} from '../types/sampleData';
import { EMPTY_SAMPLE_DATA_TEMPLATE } from '../types/sampleData';

/**
 * A stable key for a new template row. The tail of the UUID, not the head: the test setup's
 * `randomUUID` shim is zero-padded at the front, so the head would collide.
 */
export const newSampleKey = (prefix: string): string =>
  `${prefix}-${crypto.randomUUID().replace(/-/g, '').slice(-12)}`;

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown): string => (typeof value === 'string' ? value : '');

const num = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const records = (value: unknown): UnknownRecord[] =>
  Array.isArray(value) ? value.filter(isRecord) : [];

const keyOr = (value: unknown, prefix: string): string => str(value) || newSampleKey(prefix);

const toGame = (raw: UnknownRecord): SampleGame => ({
  key: keyOr(raw.key, 'game'),
  dayOffset: num(raw.dayOffset),
  time: str(raw.time),
  fieldName: str(raw.fieldName),
  homeTeamName: str(raw.homeTeamName),
  awayTeamName: str(raw.awayTeamName),
});

const toEvent = (raw: UnknownRecord): SampleEvent => ({
  key: keyOr(raw.key, 'event'),
  title: str(raw.title),
  startDayOffset: num(raw.startDayOffset),
  endDayOffset: num(raw.endDayOffset),
  games: records(raw.games).map(toGame),
});

const toTeam = (raw: UnknownRecord): SampleTeam => ({ key: keyOr(raw.key, 'team'), name: str(raw.name) });

const toPlayer = (raw: UnknownRecord): SamplePlayer => ({
  key: keyOr(raw.key, 'player'),
  firstName: str(raw.firstName),
  lastName: str(raw.lastName),
  position: str(raw.position),
  gradYear: typeof raw.gradYear === 'number' && Number.isFinite(raw.gradYear) ? raw.gradYear : null,
  phone: str(raw.phone),
  email: str(raw.email),
  teamKey: str(raw.teamKey) || null,
});

const toPlacement = (raw: UnknownRecord): SampleRosterPlacement => ({
  playerKey: str(raw.playerKey),
  position: str(raw.position),
  sortOrder: num(raw.sortOrder),
});

const toRosterBoard = (raw: unknown): SampleRosterBoard | null =>
  isRecord(raw) ? { name: str(raw.name), placements: records(raw.placements).map(toPlacement) } : null;

const toCall = (raw: UnknownRecord): SampleCall => ({
  key: keyOr(raw.key, 'call'),
  playerKey: str(raw.playerKey),
  dayOffset: num(raw.dayOffset),
  hour: num(raw.hour),
});

const toNote = (raw: UnknownRecord): SampleNote => ({
  key: keyOr(raw.key, 'note'),
  playerKey: str(raw.playerKey),
  content: str(raw.content),
});

const toDuplicatePair = (raw: unknown): [string, string] | null =>
  Array.isArray(raw) && raw.length === 2 ? [str(raw[0]), str(raw[1])] : null;

/**
 * Coerces whatever the API sent into a complete template.
 *
 * Web and API deploy separately, so this web build can meet an API that predates a field. A missing
 * array becomes `[]` and a missing scalar its empty value, rather than a crash deep in the form.
 */
export const normalizeSampleDataTemplate = (raw: unknown): SampleDataTemplateResponse => {
  const body = isRecord(raw) ? raw : {};
  const content = isRecord(body.content) ? body.content : {};
  const normalized: SampleDataTemplateContent = {
    timeZone: str(content.timeZone) || EMPTY_SAMPLE_DATA_TEMPLATE.timeZone,
    teams: records(content.teams).map(toTeam),
    players: records(content.players).map(toPlayer),
    rosterBoard: toRosterBoard(content.rosterBoard),
    calls: records(content.calls).map(toCall),
    notes: records(content.notes).map(toNote),
    events: records(content.events).map(toEvent),
    duplicatePairPlayerKeys: toDuplicatePair(content.duplicatePairPlayerKeys),
  };
  return { content: normalized, updatedAt: str(body.updatedAt) || null };
};

/** Shown when model binding rejected the body before the template validator could run. */
export const UNREADABLE_TEMPLATE_MESSAGE = 'Some values could not be read. Check numbers and times.';

/**
 * Reads the field errors out of a 400 body.
 *
 * The template validator answers with `{ errors: [{ field, message }] }`, which maps onto inputs.
 * A body that fails model binding (a string where a number belongs) gets ASP.NET's ProblemDetails
 * instead, where `errors` is an object keyed by JSON path. Those paths and messages are framework
 * text, not something to show next to a field, so that shape becomes one general message.
 * Returns null when the body carries neither.
 */
export const parseSampleDataFieldErrors = (body: unknown): SampleDataFieldError[] | null => {
  if (!isRecord(body)) return null;
  const { errors } = body;
  if (Array.isArray(errors)) {
    const parsed = errors
      .filter(isRecord)
      .map((e) => ({ field: str(e.field), message: str(e.message) }))
      .filter((e) => e.message);
    return parsed.length > 0 ? parsed : null;
  }
  if (isRecord(errors) && Object.keys(errors).length > 0) {
    return [{ field: '', message: UNREADABLE_TEMPLATE_MESSAGE }];
  }
  return null;
};
